/**
 * Holds the current active business information, and allows for easy switching between businesses
 */

import { authClient } from '@/lib/auth/client'
import { queryClient } from '@/lib/query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useBusinessInfoQuery } from './queries/useBusinessInfo'

interface ActiveBusinessState {
  // Current active business
  activeBusinessId: number | null
  setActiveBusiness: (businessId: number | null) => void
}

const STORAGE_KEY = 'rocketrank-active-business'

export const useActiveBusinessStore = create<ActiveBusinessState>()(
  persist(
    (set) => ({
      activeBusinessId: null,
      setActiveBusiness: (businessId: number | null) => {
        set({ activeBusinessId: businessId })
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({
        activeBusinessId: state.activeBusinessId,
      }),
    },
  ),
)

export const setActiveBusinessId = (businessId: number | null) => {
  useActiveBusinessStore.setState({ activeBusinessId: businessId })
}

export const getActiveBusinessId = () => {
  return useActiveBusinessStore.getState().activeBusinessId
}

export const ClearBusinessStoreDataOnLogout = () => {
  const { data: session, isPending } = authClient.useSession()

  useEffect(() => {
    if (session?.session === undefined && !isPending) {
      useActiveBusinessStore.persist.clearStorage()
      useActiveBusinessStore.setState({ activeBusinessId: null })
    }
  }, [session, isPending])

  return null
}

export const EnsureActiveBusinessWrapper = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { activeBusiness, isLoading } = useActiveBusiness()
  const { businesses, isFetching } = useBusinessInfoQuery()
  const navigate = useNavigate()

  useEffect(() => {
    if (activeBusiness) {
      queryClient.invalidateQueries()
    }
  }, [activeBusiness?.id])

  useEffect(() => {
    // Wait for data to load
    if (isLoading || !businesses || isFetching) return
    console.log(businesses)

    // If no businesses exist, redirect to onboarding
    if (businesses.length === 0) {
      navigate({ to: '/onboarding', replace: true })
    }
  }, [businesses, isLoading, navigate, isFetching])

  // Show nothing while loading
  if (isLoading || !businesses) {
    return null
  }

  // Show nothing if no businesses (will redirect via useEffect)
  if (businesses.length === 0) {
    return null
  }

  // Show nothing if no active business yet (will be auto-selected via useActiveBusiness)
  if (!activeBusiness) {
    return null
  }

  // All good - show children
  return children
}

// Hook for easy access to the active business
export const useActiveBusiness = () => {
  const store = useActiveBusinessStore()
  // ensured to be loaded because of /_dashboard beforeLoad
  const { businesses } = useBusinessInfoQuery()
  const [isLoading, setIsLoading] = useState(true)

  const activeBusiness = businesses?.find(
    (business) => business.id === store.activeBusinessId,
  )

  useEffect(() => {
    if (businesses) {
      setIsLoading(false)
      // Auto-select first business if none is selected
      if (!activeBusiness && businesses.length > 0) {
        store.setActiveBusiness(businesses[0].id)
      }
    }
  }, [businesses, store.activeBusinessId])

  return {
    activeBusiness,
    setActiveBusiness: store.setActiveBusiness,
    isLoading,
  }
}
